import { useEffect, useMemo, useState, useCallback } from "react";
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  format,
  isSameMonth,
  isSameDay,
  parseISO,
} from "date-fns";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useMembers } from "@/context/MemberContext";
import { accentHex } from "@/lib/constants";
import { PageHeader } from "@/components/shared";
import { EventDialog } from "@/components/calendar/EventDialog";
import { cn } from "@/lib/utils";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function toLocalInput(iso) {
  const d = iso ? new Date(iso) : new Date();
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
}

export default function CalendarPage() {
  const { members, memberById } = useMembers();
  const [month, setMonth] = useState(() => new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(() => new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setEvents(await api("/events"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const gridDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(month), { weekStartsOn: 0 });
    const end = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
    return eachDayOfInterval({ start, end });
  }, [month]);

  const eventsOn = useCallback(
    (day) => events.filter((e) => isSameDay(parseISO(e.start_date), day)).sort((a, b) => a.start_date.localeCompare(b.start_date)),
    [events]
  );

  const colorFor = (e) => accentHex(memberById(e.member_id)?.color || e.color);

  const openAdd = (day) => {
    setEditing(null);
    setForm({
      title: "",
      start: toLocalInput(day.toISOString()),
      all_day: false,
      member_id: "none",
      location: "",
    });
    setDialogOpen(true);
  };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      title: e.title,
      start: toLocalInput(e.start_date),
      all_day: e.all_day,
      member_id: e.member_id || "none",
      location: e.location || "",
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Add a title");
    setSaving(true);
    const start = new Date(form.start);
    const body = {
      title: form.title.trim(),
      start_date: start.toISOString(),
      end_date: new Date(start.getTime() + 3600000).toISOString(),
      all_day: form.all_day,
      member_id: form.member_id === "none" ? null : form.member_id,
      location: form.location.trim() || null,
      source: "local",
    };
    try {
      if (editing) await api(`/events/${editing.id}`, { method: "PUT", body });
      else await api("/events", { method: "POST", body });
      setDialogOpen(false);
      await load();
      toast.success(editing ? "Event updated" : "Event added");
    } catch {
      toast.error("Could not save event");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    try {
      await api(`/events/${editing.id}`, { method: "DELETE" });
      setDialogOpen(false);
      await load();
      toast.success("Event deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const selectedEvents = eventsOn(selected);

  return (
    <div>
      <PageHeader
        title="Calendar"
        subtitle="Everyone's schedule in one place"
        action={
          <Button className="rounded-full" onClick={() => openAdd(selected)} data-testid="add-event-btn">
            <Plus className="mr-1.5 h-4 w-4" /> Event
          </Button>
        }
      />

      <div className="mt-4 flex items-center justify-between">
        <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setMonth((m) => addMonths(m, -1))} data-testid="month-prev-btn">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <span className="font-display text-lg font-semibold text-ink">{format(month, "MMMM yyyy")}</span>
        <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setMonth((m) => addMonths(m, 1))} data-testid="month-next-btn">
          <ChevronRight className="h-5 w-5" />
        </Button>
      </div>

      {loading ? (
        <Skeleton className="mt-4 h-96 w-full rounded-3xl" />
      ) : (
        <div className="bento-tile mt-4 overflow-hidden p-2 sm:p-3">
          <div className="grid grid-cols-7">
            {WEEKDAYS.map((d) => (
              <div key={d} className="py-2 text-center text-xs font-semibold text-muted-foreground">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {gridDays.map((day) => {
              const dayEvents = eventsOn(day);
              const inMonth = isSameMonth(day, month);
              const isSel = isSameDay(day, selected);
              const isToday = isSameDay(day, new Date());
              return (
                <button
                  key={day.toISOString()}
                  onClick={() => setSelected(day)}
                  data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                  className={cn(
                    "flex min-h-[64px] flex-col items-stretch gap-1 rounded-xl p-1.5 text-left transition-colors sm:min-h-[84px]",
                    isSel ? "bg-accent" : "hover:bg-secondary/60",
                    !inMonth && "opacity-40"
                  )}
                >
                  <span
                    className={cn(
                      "flex h-6 w-6 items-center justify-center self-start rounded-full text-xs font-semibold",
                      isToday ? "bg-primary text-primary-foreground" : "text-ink"
                    )}
                  >
                    {format(day, "d")}
                  </span>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 2).map((e) => (
                      <div
                        key={e.id}
                        className="truncate rounded-md px-1 py-0.5 text-[10px] font-medium text-ink"
                        style={{ backgroundColor: `${colorFor(e)}26`, borderLeft: `3px solid ${colorFor(e)}` }}
                      >
                        {e.title}
                      </div>
                    ))}
                    {dayEvents.length > 2 && (
                      <div className="px-1 text-[10px] text-muted-foreground">+{dayEvents.length - 2} more</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected day events */}
      <div className="mt-6">
        <h2 className="mb-3 font-display text-lg font-semibold text-ink">{format(selected, "EEEE, MMMM d")}</h2>
        {selectedEvents.length === 0 ? (
          <p className="rounded-2xl bg-secondary/40 px-4 py-6 text-center text-sm text-muted-foreground">
            Nothing scheduled. Tap “Event” to add one.
          </p>
        ) : (
          <div className="space-y-2.5">
            {selectedEvents.map((e) => {
              const mem = memberById(e.member_id);
              return (
                <button
                  key={e.id}
                  onClick={() => openEdit(e)}
                  data-testid={`event-card-${e.id}`}
                  className="bento-tile flex w-full items-center gap-3 p-4 text-left"
                  style={{ borderLeft: `4px solid ${colorFor(e)}` }}
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display font-semibold text-ink">{e.title}</p>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
                      <span>{e.all_day ? "All day" : format(parseISO(e.start_date), "h:mm a")}</span>
                      {mem && <span>· {mem.name}</span>}
                      {e.location && (
                        <span className="inline-flex items-center gap-0.5">
                          <MapPin className="h-3 w-3" /> {e.location}
                        </span>
                      )}
                      {e.source !== "local" && <span className="rounded bg-secondary px-1.5 py-0.5 uppercase">{e.source}</span>}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <EventDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        form={form}
        setForm={setForm}
        members={members}
        saving={saving}
        onSave={save}
        onDelete={remove}
      />
    </div>
  );
}
